"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import DecorationLine from "@/components/global/ui/DecorationLine";
import EmbedCode from "@/components/global/utils/EmbedCode";
import { resolveVideoSourceType } from "@/sanity/globals/VideoPlayer";

import { PageVslLongSchema } from "@/sanity/pages/PageVslLongTemplate";

export default function HeroSection({
  data,
  className,
}: {
  data: PageVslLongSchema;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [started, setStarted] = useState(false);

  // Stable reference so a Sanity live-refresh (new object, same playbackId)
  // doesn't re-attach the HLS stream and reset the playhead to 0:00.
  const heroVideoSource = useMemo(
    () => data?.heroVideo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data?.heroVideo?.sourceType,
      data?.heroVideo?.muxVideo?.playbackId,
      data?.heroVideo?.embedUrl,
      data?.heroVideo?.htmlCode,
    ],
  );

  const videoSourceType = resolveVideoSourceType(heroVideoSource);
  const hasHtmlEmbed =
    videoSourceType === "html" && Boolean(heroVideoSource?.htmlCode?.trim());
  const hasEmbedUrl =
    videoSourceType === "embedUrl" && Boolean(heroVideoSource?.embedUrl);
  const hasMuxVideo =
    videoSourceType === "mux" &&
    Boolean(heroVideoSource?.muxVideo?.playbackId);

  useEffect(() => {
    // keep the inline preview muted so autoplay is allowed
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  // play from the start with audio + native controls when the user clicks
  const handleStart = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.muted = false;
    video.play();
    setIsMuted(false);
    setStarted(true);
  };

  return (
    <section
      className={cn(
        "relative flex flex-col items-center px-[2rem] pt-[6rem] pb-[9rem] sm:px-[3rem] md:px-[5rem] lg:pt-[9rem] lg:pb-[14rem]",
        className,
      )}
    >
      {/* horizontal divider under the header */}
      <DecorationLine className="top-0" orientation="horizontal" style="dark" />

      {/* vertical side lines flanking the content (near edges on mobile, matching Figma inset on desktop) */}
      <DecorationLine
      className="top-0"
        orientation="vertical"
        position="left"
        style="dark"
      />
      <DecorationLine
      className="top-0"
        orientation="vertical"
        position="right"
        style="dark"
      />

      {/* eyebrow chip */}
      {data?.heroChip?.text && (
        <Chip
          className="mb-[3rem] [&_p]:uppercase"
          image={data?.heroChip?.icon}
          text={data?.heroChip?.text}
        />
      )}

      {/* headline */}
      {data?.heroTitle && (
        <div className="pixel-highlight richtext richtext-center w-[min(113.75rem,100%)] lg:w-[min(140rem,100%)] prose-headings:font-funnel prose-headings:font-medium prose-headings:text-black prose-headings:text-[5.5rem] sm:prose-headings:text-[6.5rem] lg:prose-headings:text-[7rem] prose-headings:leading-[1.1] prose-headings:text-balance prose-em:font-ivy">
          <PortableText
            value={data.heroTitle}
            components={richTextComponent}
          />
        </div>
      )}

      {/* subtext */}
      {data?.heroText && (
        <div className="richtext richtext-center mt-[2rem] w-[min(63rem,100%)] prose-p:text-black/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.5]">
          <PortableText
            value={data.heroText}
            components={richTextComponent}
          />
        </div>
      )}

      {/* HTML embed — third-party players ship their own UI */}
      {hasHtmlEmbed ? (
        <div className="relative mt-[6rem] w-[min(133rem,100%)] overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
          <EmbedCode code={heroVideoSource?.htmlCode} />
        </div>
      ) : hasEmbedUrl ? (
        <div className="relative mt-[6rem] w-[min(133rem,100%)] aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
          <iframe
            src={heroVideoSource?.embedUrl}
            title={data?.heroPlayLabel || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute left-0 top-0 h-full w-full border-0"
          />
        </div>
      ) : /* mux video — autoplays muted inline; click restarts from 0 with audio */
      hasMuxVideo ? (
        <div className="relative mt-[6rem] w-[min(133rem,100%)] aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
          <HLSVideoPlayer
            ref={videoRef}
            source={heroVideoSource}
            autoPlay={!started}
            muted={isMuted}
            loop={!started}
            playsInline
            controls={started}
            poster={data?.heroVideoPlaceholder?.src}
            className="absolute left-0 top-0 !h-full !w-full object-cover"
          />

          {!started && (
            <button
              onClick={handleStart}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 p-[0.5rem] hover:cursor-pointer"
              aria-label={data?.heroPlayLabel || "Video ansehen"}
            >
              <span className="flex h-[7rem] items-center justify-center gap-[1rem] rounded-full bg-[#D9F9B9] px-[3rem] py-[1rem]">
                <svg
                  className="h-[2rem] w-[2rem]"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4 2.5v11l9-5.5-9-5.5z" fill="rgba(0,0,0,0.8)" />
                </svg>
                <span className="font-geist text-[2.25rem] font-medium leading-none text-black/80 whitespace-nowrap">
                  {data?.heroPlayLabel || "Video ansehen"}
                </span>
              </span>
            </button>
          )}
        </div>
      ) : (
        data?.heroVideoPlaceholder?.src && (
          <div className="relative mt-[6rem] w-[min(133rem,100%)] aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
            <Image
              className="absolute left-0 top-0 !h-full !w-full object-cover"
              src={data.heroVideoPlaceholder}
              alt=""
            />
          </div>
        )
      )}
    </section>
  );
}
